// Corkboard layouts (SuiteModel/BoardLayout.swift, and the app's CorkboardBoards.swift, StructureViews.swift,
// LibraryModel+Board.swift and PhonePlanPage.swift's `planChildren`): where unplaced cards go on a freeform board, the
// order the board reads in, the first drop that pins every card, Tidy Up, a part's chapters as columns, and swimlanes.
import type { EntityKind, Field, MetadataValue, Opener } from '../contract.ts';
import type { ArrangementTreeNode } from '../model/arrangement.ts';
import type { StoryEntity } from '../model/documents.ts';
import { standardCompare, swiftCompare } from '../model/order.ts';

/** `BoardPoint`: a card's place on a freeform board (`boardX`/`boardY` on the node). */
export interface BoardPoint { x: number; y: number }

export const CARD_WIDTH = 220;
export const CARD_HEIGHT = 170;
export const SPACING = 24;

/** `BoardLayout.points(for:placed:columns:)`: placed cards keep their points; the rest go in a grid below them
 *  (`columns` wide), in the order given. */
export function boardPoints(ids: string[], placed: ReadonlyMap<string, BoardPoint>, columns: number): Map<string, BoardPoint> {
  const result = new Map<string, BoardPoint>();
  const kept = ids.filter((id) => placed.has(id));
  for (const id of kept) result.set(id, placed.get(id)!);
  const tops = kept.map((id) => placed.get(id)!.y + CARD_HEIGHT + SPACING);
  const top = tops.length ? Math.max(...tops) : SPACING;
  const cols = Math.max(1, Math.trunc(columns));
  ids.filter((id) => !placed.has(id)).forEach((id, i) => {
    result.set(id, { x: SPACING + (i % cols) * (CARD_WIDTH + SPACING), y: top + Math.floor(i / cols) * (CARD_HEIGHT + SPACING) });
  });
  return result;
}

/** How many columns of cards fit in `width` points (the freeform board's grid below the placed cards). */
export const boardColumns = (width: number) => Math.max(1, Math.floor((width - SPACING) / (CARD_WIDTH + SPACING)));

/** `BoardLayout.readingOrder`: rows top to bottom (cards whose tops are within half a card's height share a row), each
 *  left to right. */
export function readingOrder(points: ReadonlyMap<string, BoardPoint> | Record<string, BoardPoint>): string[] {
  const entries = (points instanceof Map ? [...points] : Object.entries(points)) as [string, BoardPoint][];
  const byTop = entries.sort(([ka, a], [kb, b]) => a.y - b.y || a.x - b.x || swiftCompare(ka, kb));
  const rows: [string, BoardPoint][][] = [];
  for (const entry of byTop) {
    const first = rows.at(-1)?.[0];
    if (first && entry[1].y - first[1].y < CARD_HEIGHT / 2) rows[rows.length - 1].push(entry);
    else rows.push([entry]);
  }
  return rows.flatMap((row) => row.sort(([ka, a], [kb, b]) => a.x - b.x || swiftCompare(ka, kb)).map(([id]) => id));
}

/** The cards' stored places (`ArrangementNode.board`), by node id. */
export function placedPoints(nodes: Iterable<{ id: string; board?: BoardPoint }>): Map<string, BoardPoint> {
  const out = new Map<string, BoardPoint>();
  for (const n of nodes) if (n.board) out.set(n.id, n.board);
  return out;
}

/** `FreeformBoard.drop`: a card dropped at `point`. The first move on a board fixes every card where it is (every card
 *  not yet placed takes the point it's shown at), so the grid below doesn't reflow. `points` = what's shown. */
export function dropChanges(id: string, point: BoardPoint, points: ReadonlyMap<string, BoardPoint>, placed: ReadonlyMap<string, BoardPoint>):
  Map<string, BoardPoint | null> {
  const changes = new Map<string, BoardPoint | null>();
  for (const [other, p] of points) if (!placed.has(other) && other !== id) changes.set(other, p);
  changes.set(id, point);
  return changes;
}

/** `FreeformBoard.nudge` (the accessibility moves): a card a whole card's width or height over, then dropped. */
export function nudgeChanges(id: string, dx: number, dy: number, points: ReadonlyMap<string, BoardPoint>, placed: ReadonlyMap<string, BoardPoint>):
  Map<string, BoardPoint | null> | null {
  const p = points.get(id);
  if (!p) return null;
  return dropChanges(id, { x: Math.max(0, p.x + dx * (CARD_WIDTH + SPACING)), y: Math.max(0, p.y + dy * (CARD_HEIGHT + SPACING)) }, points, placed);
}

/** Tidy Up: every card taken off the board, to be laid out in the grid again. */
export const tidyUpChanges = (nodeIDs: Iterable<string>) => new Map<string, BoardPoint | null>([...nodeIDs].map((id) => [id, null]));

// MARK: The grid

/** `planChildren(in:)`: what Plan shows — the selected folder's items, or the selected document's siblings (the top
 *  level with nothing selected) — and the folder they're in. */
export function planChildren(tree: ArrangementTreeNode[], selectedNodeID: string | null):
  { container: ArrangementTreeNode['node'] | null; nodes: ArrangementTreeNode[] } {
  if (selectedNodeID === null) return { container: null, nodes: tree };
  const find = (nodes: ArrangementTreeNode[], parent: ArrangementTreeNode['node'] | null):
    { node: ArrangementTreeNode; siblings: ArrangementTreeNode[]; parent: ArrangementTreeNode['node'] | null } | null => {
    for (const n of nodes) {
      if (n.node.id === selectedNodeID) return { node: n, siblings: nodes, parent };
      const hit = find(n.children, n.node);
      if (hit) return hit;
    }
    return null;
  };
  const hit = find(tree, null);
  if (!hit) return { container: null, nodes: tree };
  return hit.node.children.length === 0 ? { container: hit.parent, nodes: hit.siblings } : { container: hit.node.node, nodes: hit.node.children };
}

/** Whether the grid shows the cards as a part's chapters, each a column of its scenes: every shown card has children. */
export const showsChapterColumns = (nodes: ArrangementTreeNode[]) => nodes.length > 0 && nodes.every((n) => n.children.length > 0);

/** `columnTitle`: "Twelve · The Weir" — the chapter's number as compile numbers it, and its title. */
export function columnTitle(title: string, opener: Pick<Opener, 'label'> | null | undefined): string {
  if (!opener?.label) return title; // an unnumbered chapter (a prologue) is its title alone
  const space = opener.label.indexOf(' ');
  const number = space >= 0 ? opener.label.slice(space + 1) : opener.label;
  return `${number} · ${title}`;
}

/** Where a card dropped on another goes: just before it, among the siblings other than the one dragged. */
export function dropBeforeIndex(siblings: string[], dragged: string, target: string): number {
  const others = siblings.filter((id) => id !== dragged);
  const i = others.indexOf(target);
  return i >= 0 ? i : others.length;
}

/** "Part Two · 8 scenes": the leaves under the shown cards. */
export function leafCount(nodes: ArrangementTreeNode[]): number {
  return nodes.reduce((n, t) => n + (t.children.length === 0 ? 1 : leafCount(t.children)), 0);
}

// MARK: Swimlanes

/** `BoardLayout.Card` and `Lane`. */
export interface LaneCard { id: string; column: number }
export interface Lane { value: string | null; title: string; cards: LaneCard[] }

/** `BoardLayout.lanes`: "none" first (the scenes still to sort), then one lane per value in `order` that has scenes (all
 *  of them when `keepEmpty`), then values not in `order` (sorted). Each card's column is its place in reading order across every lane. */
export function lanes(scenes: { id: string; value: string | null | undefined }[], order: { value: string; title: string }[],
  noneTitle: string, keepEmpty = true): Lane[] {
  const byValue = new Map<string, LaneCard[]>();
  const none: LaneCard[] = [];
  scenes.forEach((scene, column) => {
    const card = { id: scene.id, column };
    if (scene.value) byValue.set(scene.value, [...(byValue.get(scene.value) ?? []), card]);
    else none.push(card);
  });
  const out: Lane[] = none.length || keepEmpty ? [{ value: null, title: noneTitle, cards: none }] : [];
  for (const entry of order) {
    const cards = byValue.get(entry.value) ?? [];
    byValue.delete(entry.value);
    if (cards.length || keepEmpty) out.push({ value: entry.value, title: entry.title, cards });
  }
  for (const value of [...byValue.keys()].sort(swiftCompare)) out.push({ value, title: value, cards: byValue.get(value)! });
  return out;
}

/** `laneFields`: the fields a swimlane board can split by — enumerations with options, and entity references. */
export const laneFields = <F extends Pick<Field, 'type' | 'options'>>(fields: F[]): F[] =>
  fields.filter((f) => (f.type === 'enum' && f.options.length > 0) || f.type === 'entity-ref');

/** `laneOrder`: a field's values in the order lanes show them — options as defined; for an entity reference the
 *  characters (POV), places (setting) or every entity, by name. */
export function laneOrder(field: Pick<Field, 'id' | 'type' | 'options'>, entities: Pick<StoryEntity, 'id' | 'kind' | 'name'>[]): { value: string; title: string }[] {
  if (field.type === 'enum') return field.options.map((o) => ({ value: o.id, title: o.name }));
  const kind: EntityKind | undefined = field.id === 'pov' ? 'character' : field.id === 'setting' ? 'place' : undefined;
  return entities.filter((e) => kind === undefined || e.kind === kind)
    .sort((a, b) => standardCompare(a.name, b.name))
    .map((e) => ({ value: e.id, title: e.name }));
}

/** `rawValue(_:field:)`: a document's stored value for a field (an option or entity id), when it's non-empty text. */
export function rawValue(metadata: Record<string, MetadataValue> | undefined, field: string): string | null {
  const v = metadata?.[field];
  return v?.type === 'text' && v.value !== '' ? v.value : null;
}

/** A scene on the swimlane board: a leaf of the arrangement, with the title of the folder it's in. */
export interface LaneScene { nodeID: string; fragmentID: string; chapter: string | null }

/** `SwimlaneBoard.scenes()`: every leaf (no children) of the arrangement with a fragment that isn't trashed, in reading
 *  order, with its parent folder's title. */
export function swimlaneScenes(tree: ArrangementTreeNode[], title: (node: ArrangementTreeNode['node']) => string, trashed: (fragmentID: string) => boolean): LaneScene[] {
  const out: LaneScene[] = [];
  const walk = (nodes: ArrangementTreeNode[], chapter: string | null) => {
    for (const n of nodes) {
      if (n.children.length === 0) {
        const fid = n.node.fragmentID;
        if (fid !== undefined && !trashed(fid)) out.push({ nodeID: n.node.id, fragmentID: fid, chapter });
      } else {
        walk(n.children, title(n.node));
      }
    }
  };
  walk(tree, null);
  return out;
}

/** The chapter header over the shared columns: a column's chapter name where a chapter begins, else "". */
export const chapterHeader = (scenes: LaneScene[]) => scenes.map((s, i) => (i === 0 || scenes[i - 1].chapter !== s.chapter ? s.chapter ?? '' : ''));

/** The whole swimlane board for a field: lanes (by fragment id), in the app's order, with "No <field>" last. */
export function swimlaneBoard(scenes: LaneScene[], field: Pick<Field, 'id' | 'name' | 'type' | 'options'>,
  metadataOf: (fragmentID: string) => Record<string, MetadataValue> | undefined, entities: Pick<StoryEntity, 'id' | 'kind' | 'name'>[], keepEmpty = false): Lane[] {
  return lanes(scenes.map((s) => ({ id: s.fragmentID, value: rawValue(metadataOf(s.fragmentID), field.id) })),
    laneOrder(field, entities), `No ${field.name}`, keepEmpty);
}

/** A drop on a lane: the dropped scenes (on the board) whose value differs from the lane's; set the field to the lane's
 *  value (null clears it) on these. */
export function laneDropTargets(dropped: string[], lane: Pick<Lane, 'value'>, field: string, onBoard: ReadonlySet<string>,
  metadataOf: (fragmentID: string) => Record<string, MetadataValue> | undefined): string[] {
  return dropped.filter((id) => onBoard.has(id) && rawValue(metadataOf(id), field) !== lane.value);
}
